<script setup>
import {formatUrl, viewportSize} from '@teemill/common/helpers';
import {
  faPerson,
  faPersonDress,
  faChild,
  faFamily,
} from '@fortawesome/pro-light-svg-icons';
import {startCase} from '@teemill/utilities';
import {uniq} from 'lodash';

import {computed, ref, watch, inject} from 'vue';
import {useStore} from 'vuex';

const store = useStore();

const theme = inject('theme');

const props = defineProps({
  products: Array,
  maxColorSwatches: {
    type: Number,
    default: 5,
  },
  podHorizontalAlignment: String,
});

const hasReviewsEnabled = computed(() => {
  if (store.state.subdomain.reviews === null) {
    return true;
  }

  return !!store.state.subdomain.reviews?.enabled;
});

const renderLimit = ref(4);

const filteredProducts = computed(() => {
  if (!('requestIdleCallback' in window)) {
    return props.products;
  }

  return props.products.slice(0, renderLimit.value);
});

function getLongProductName(product) {
  if (product.defaultColor) {
    return `${startCase(product.defaultColor)} ${product.name}`;
  }
  return product.name;
}

function getProductHref(product) {
  if (product.productHref) {
    return product.productHref;
  }

  const href = `/product/${product.urlName}/`;

  if (product.defaultColor) {
    return `${href}${encodeURIComponent(product.defaultColor)}/`;
  }

  return href;
}

function onIdle(callback) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, {
      timeout: 10000,
    });
  } else {
    setTimeout(callback, 100);
  }
}

function increaseRenderLimit() {
  renderLimit.value += 4;

  if (renderLimit.value < props.products.length) {
    onIdle(increaseRenderLimit);
  }
}

onIdle(increaseRenderLimit);

watch(
  () => props.products?.length,
  () => {
    renderLimit.value = 4;
    onIdle(increaseRenderLimit);
  }
);

const activePrice = product => {
  if (product.isBundle) {
    return product.price;
  }

  if (product.priceRange) {
    return Math.min(...product.priceRange);
  }

  return product.price;
};

const salePrice = product => {
  if (product.isBundle) {
    return product.salePrice;
  }
  const nonZeroSalePriceRange = (product.salePriceRange || []).filter(
    price => price > 0
  );

  if (nonZeroSalePriceRange.length > 0) {
    return Math.min(...nonZeroSalePriceRange);
  }

  return product.salePrice;
};

const toPrice = product => {
  if (product.isBundle) {
    return product.price;
  }

  if (product.priceRange) {
    return Math.max(...product.priceRange);
  }

  return product.price;
};

const showToPrice = product => {
  if (salePrice(product) > 0) {
    return false;
  }

  return hasVariantPricing(product);
};

const colorSwatch = (product, color) => {
  const thumbnail = product?.colorThumbnails?.[color];

  if (!thumbnail) {
    return color;
  }

  let background;

  if (thumbnail?.type === 'image') {
    background = `url(${formatUrl(thumbnail.value)})`;
  } else if (thumbnail?.type === 'color') {
    background = thumbnail.value;
  }

  return {
    text: color,
    value: color,
    name: color,
    background: background,
  };
};

const hasVariantPricing = product => {
  if (product.isBundle) {
    return false;
  }

  return (
    (product.priceRange && uniq(product.priceRange).length > 1) ||
    (product.salePriceRange && uniq(product.salePriceRange).length > 1)
  );
};
</script>

<template>
  <div class="section-product-list mb-8">
    <tml-grid :breakpoints="{xs: 2, sm: 2, lg: 4}">
      <div
        v-for="product in filteredProducts"
        :key="`${product.id}-${product.optionId || 0}`"
        v-memo="[product.id, product.collectionImage, podHorizontalAlignment]"
      >
        <tml-new-pod
          :title="product.name"
          :alt-text="product.collectionImageAlt || getLongProductName(product)"
          :title-size="viewportSize.isSmaller('md') ? 0 : 1"
          :image="product.collectionImage"
          :href="getProductHref(product)"
          :price="activePrice(product)"
          :sale-price="salePrice(product)"
          :price-prefix="hasVariantPricing(product) ? 'From\u00A0' : undefined"
          :to-price="showToPrice(product) ? toPrice(product) : undefined"
          lazy-load
          :image-src-set="[
            {width: 320, height: 337},
            {width: 480, height: 505},
            {width: 640, height: 674},
          ]"
          :horizontal-alignment="podHorizontalAlignment"
          @overlay-icon-clicked="attemptToFavourite(product.id)"
        >
          <template
            v-if="product.reviews?.count && hasReviewsEnabled"
            #after-title
          >
            <div
              class="w-full gap-2 items-center cursor-pointer no-hover text-sm"
            >
              <tml-star-rating
                class="inline mr-1"
                :rating="product.reviews.rating"
                :max="5"
              />
            </div>
          </template>
        </tml-new-pod>
        <div class="relative min-h-[2rem]">
          <div
            v-if="product.colors?.length > 1"
            class="inset-0 flex flex-wrap gap-1 max-w-[16em]"
          >
            <div
              v-for="(color, index) in product.colors
                .slice(0, maxColorSwatches)
                .map(color => {
                  return typeof color === 'string' ? {name: color} : color;
                })"
              :key="color.name"
            >
              <tml-anchor
                :href="
                  color.href ||
                    `/product/${product.urlName}/${encodeURIComponent(
                      color.name
                    )}/`
                "
              >
                <tml-color-square
                  class="w-4 !h-4 rounded border-[1px] border-solid border-gray-200 overflow-hidden"
                  :style="{
                    borderRadius: theme().get(
                      'productPage.color.selector.border.radius'
                    ),
                  }"
                  :color="colorSwatch(product, color.name)"
                  role="button"
                  :title="color.name"
                  disable-tooltip
                />
              </tml-anchor>
            </div>
            <tml-anchor
              v-if="product.colors.length > maxColorSwatches"
              :href="`/product/${product.urlName}/`"
              class="text-xs sm:text-sm ml-1 !leading-none !text-[--tml-text-color] opacity-70"
            >
              +{{ product.colors.length - maxColorSwatches }}
            </tml-anchor>
          </div>
        </div>
      </div>
    </tml-grid>
  </div>
</template>
